export interface UseEntitySelectionArgs<T> {
  /** Selected ids. Single-select callers pass `[id]` or `[]`. */
  value: number[];
  /** Current (debounced) search query. */
  query: string;
  /** Hook returning the current page of search results for the query. */
  useSearchResults: (query: string) => {
    data: T[] | undefined;
    isLoading: boolean;
  };
  /** Hook resolving selected ids → full entities, independent of the query. */
  useResolveSelected: (ids: number[]) => { data: T[] | undefined };
  getId: (entity: T) => number;
}

export interface EntitySelection<T> {
  /** Resolved selection — render pinned at the top, and label from these. */
  selectedEntities: T[];
  /** Search results with the pinned set removed (deduped). */
  results: T[];
  isLoading: boolean;
}

/**
 * Shared selection core for entity selects (single- and multi-select).
 *
 * Owns the invariant that a selection is resolved independently of the
 * search/pagination window: `selectedEntities` always reflects the current
 * `value` even when those entities are off the current search page, and
 * `results` never double-lists a pinned entity.
 */
export function useEntitySelection<T>({
  value,
  query,
  useSearchResults,
  useResolveSelected,
  getId,
}: UseEntitySelectionArgs<T>): EntitySelection<T> {
  const search = useSearchResults(query);
  const resolved = useResolveSelected(value);

  // selectedEntities must mirror `value` exactly — never surface stale resolve
  // data. A resolver backed by useItems keeps its previous results via
  // keepPreviousData even once its query is disabled (empty ids), which would
  // otherwise leave a phantom selection that can't be cleared or reset.
  const valueIds = new Set(value);
  const selectedEntities = (resolved.data ?? []).filter((e) =>
    valueIds.has(getId(e)),
  );
  const pinnedIds = new Set(selectedEntities.map(getId));
  const results = (search.data ?? []).filter((e) => !pinnedIds.has(getId(e)));

  return { selectedEntities, results, isLoading: search.isLoading };
}
